import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Smartphone, Eye, EyeOff, Loader2, TrendingUp } from "lucide-react";
import { LEVELS, LEVEL_BONUS } from "@/lib/levels";
import { LevelUpShow } from "@/components/LevelUpShow";
import { confetti, playFanfare } from "@/lib/celebrate";
import { Switch } from "@/components/ui/switch";
import { api, formatErr, imgSrc } from "@/lib/api";
import { refreshUi } from "@/lib/uiConfig";
import { AnnouncementImagePicker } from "@/components/admin/AnnouncementExtras";
import { InstallPopupView } from "@/components/InstallFeedPopup";
import { FeedOrderAdmin } from "@/components/admin/FeedOrderAdmin";

const Card = ({ children, ...p }) => <div {...p} className="bg-white rounded-3xl p-5 sm:p-6 border border-pink-100 space-y-4">{children}</div>;

export function InstallPopupAdmin() {
  const [c, setC] = useState(null);
  const [img, setImg] = useState("");
  const [busy, setBusy] = useState(false);
  const [preview, setPreview] = useState(false);
  useEffect(() => { api.get("/ui-config").then(({ data }) => setC(data.install_popup)); }, []);
  if (!c) return <Loader2 className="w-6 h-6 animate-spin text-rose-500" />;
  const save = async (patch) => {
    setBusy(true);
    try {
      const { data } = await api.put("/admin/install-popup", { enabled: c.enabled, ...patch });
      setC(data); refreshUi(); toast.success(data.enabled ? "Pop-up ist im Feed eingeschaltet" : "Pop-up ist ausgeschaltet");
    } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  return (
    <Card data-testid="admin-install-popup">
      <h3 className="font-bold text-lg flex items-center gap-2"><Smartphone className="w-5 h-5 text-rose-500" /> „App installieren“-Pop-up</h3>
      <p className="text-xs text-slate-500">Wenn eingeschaltet, erscheint das Pop-up einmal pro Tag beim Öffnen des Feeds – nur bei Mitgliedern, die die App noch nicht installiert haben. Beim Einschalten sehen es alle sofort neu, auch wer es heute schon weggeklickt hat.</p>
      <p className="text-xs font-semibold text-amber-700 bg-amber-50 rounded-xl px-3 py-2" data-testid="admin-install-popup-hint">Hinweis: In der bereits installierten App (Home-Bildschirm) erscheint das Pop-up absichtlich nicht. Zum Ansehen nutze „Vorschau ansehen“.</p>
      <label className="flex items-center gap-3 text-sm font-semibold"><Switch data-testid="admin-install-popup-switch" checked={c.enabled} disabled={busy} onCheckedChange={(v) => save({ enabled: v })} /> {c.enabled ? "Im Feed eingeschaltet" : "Ausgeschaltet"}</label>
      <img src={imgSrc(img || c.image)} alt="" className="w-full max-w-xs aspect-square object-cover rounded-2xl" data-testid="admin-install-popup-image" />
      <AnnouncementImagePicker value={img} onChange={setImg} />
      <div className="flex flex-wrap gap-2">
        <button onClick={() => save({ image: img }).then(() => setImg(""))} disabled={busy || !img} data-testid="admin-install-popup-save" className="h-10 px-5 rounded-full pink-gradient text-white text-sm font-bold disabled:opacity-50">Neues Bild speichern</button>
        <button onClick={() => setPreview(true)} data-testid="admin-install-popup-preview" className="h-10 px-5 rounded-full border-2 border-rose-200 text-rose-600 text-sm font-bold">Vorschau ansehen</button>
      </div>
      {preview && <InstallPopupView preview image={imgSrc(img || c.image)} onClose={() => setPreview(false)} />}
    </Card>
  );
}

const GROUPS = [["feed", "Im Feed"], ["feedcards", "Weitere Feed-Karten (standardmäßig aus)"], ["games", "Auf der Spiele-Seite"], ["tiles", "Spiele-Kacheln"]];

export function VisibilityAdmin() {
  const [d, setD] = useState(null);
  const [busy, setBusy] = useState("");
  useEffect(() => { api.get("/admin/ui-visibility").then(({ data }) => setD(data)); }, []);
  if (!d) return <Loader2 className="w-6 h-6 animate-spin text-rose-500" />;
  const flip = (list, id) => (list.includes(id) ? list.filter((x) => x !== id) : [...list, id]);
  const toggle = async (i) => {
    const body = i.optin ? { hidden: d.hidden, shown: flip(d.shown || [], i.id) } : { hidden: flip(d.hidden, i.id), shown: d.shown || [] };
    setBusy(i.id);
    try { const { data } = await api.put("/admin/ui-visibility", body); setD({ ...d, ...data }); refreshUi(); }
    catch (e) { toast.error(formatErr(e)); }
    setBusy("");
  };
  return (
    <Card data-testid="admin-visibility">
      <h3 className="font-bold text-lg">Spiele & Missionen anzeigen</h3>
      <p className="text-xs text-slate-500">Blende jede Karte für alle Mitglieder ein oder aus. Ausgeblendetes bleibt gespeichert und ist sofort wieder da, wenn du es einschaltest. Ein Spiel komplett pausieren kannst du zusätzlich im Reiter „Spiele“.</p>
      {GROUPS.map(([g, title]) => (
        <div key={g} className="space-y-2">
          <p className="text-xs font-bold uppercase tracking-wider text-rose-500">{title}</p>
          <div className="grid sm:grid-cols-2 gap-2">
            {d.items.filter((i) => i.group === g).map((i) => {
              const on = i.optin ? (d.shown || []).includes(i.id) : !d.hidden.includes(i.id);
              return (
                <label key={i.id} className={`flex items-center gap-3 p-3 rounded-2xl text-sm ${on ? "bg-rose-50/60" : "bg-slate-100 text-slate-400"}`} data-testid={`admin-vis-${i.id}`}>
                  <Switch checked={on} disabled={busy === i.id} onCheckedChange={() => toggle(i)} data-testid={`admin-vis-switch-${i.id}`} />
                  <span className="flex-1 font-semibold">{i.label}</span>
                  {on ? <Eye className="w-4 h-4 text-rose-400" /> : <EyeOff className="w-4 h-4" />}
                </label>
              );
            })}
          </div>
        </div>
      ))}
      <FeedOrderAdmin items={d.items} shown={d.shown || []} />
    </Card>
  );
}

export function LevelUpAdmin() {
  const [on, setOn] = useState(null);
  const [lvl, setLvl] = useState(5);
  const [show, setShow] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/ui-config").then(({ data }) => setOn(!!data.levelup_v2)); }, []);
  if (on === null) return <Loader2 className="w-6 h-6 animate-spin text-rose-500" />;
  const save = async (v) => {
    setBusy(true);
    try { await api.put("/admin/levelup-v2", { enabled: v }); setOn(v); refreshUi(); toast.success(v ? "Neue Levelaufstieg-Animation ist für alle live" : "Wieder die bisherige Animation"); } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  const preview = () => {
    const l = LEVELS[lvl - 1], next = LEVELS[lvl];
    setShow({ ...l, level: lvl, next, bonus: LEVEL_BONUS, points: l.min + Math.round(((next?.min ?? l.min) - l.min) * 0.1) });
    playFanfare(); confetti({ count: 160, spread: 2 }); setTimeout(() => confetti({ count: 120, spread: 2.4 }), 600);
  };
  return (
    <Card data-testid="admin-levelup">
      <h3 className="font-bold text-lg flex items-center gap-2"><TrendingUp className="w-5 h-5 text-rose-500" /> Levelaufstieg – neue Vollbild-Animation</h3>
      <p className="text-xs text-slate-500">Edle Vollbild-Animation mit Medaille, Lichtstrahlen und den neu freigeschalteten Vorteilen (Bonuspunkte, neuer Titel, Community-Mitteilung, nächstes Ziel). Die Vorschau sieht nur du – es werden keine Punkte vergeben.</p>
      <div className="flex flex-wrap items-center gap-2">
        <select value={lvl} onChange={(e) => setLvl(Number(e.target.value))} data-testid="admin-levelup-level" className="h-10 px-3 rounded-full border border-pink-200 text-sm font-semibold">
          {LEVELS.slice(1).map((l, i) => <option key={l.name} value={i + 2}>Level {i + 2} · {l.emoji} {l.name}</option>)}
        </select>
        <button onClick={preview} data-testid="admin-levelup-preview" className="h-10 px-5 rounded-full border-2 border-rose-200 text-rose-600 text-sm font-bold inline-flex items-center gap-1.5"><Eye className="w-4 h-4" /> Vorschau ansehen</button>
      </div>
      <label className="flex items-center gap-3 text-sm font-semibold"><Switch data-testid="admin-levelup-switch" checked={on} disabled={busy} onCheckedChange={save} /> {on ? "Veröffentlicht – alle Mitglieder sehen die neue Animation" : "Noch nicht veröffentlicht – Mitglieder sehen die bisherige Animation"}</label>
      {show && <LevelUpShow preview up={show} onClose={() => setShow(null)} />}
    </Card>
  );
}

export function WhatsAppAdmin() {
  const [url, setUrl] = useState(null);
  const [busy, setBusy] = useState(false);
  useEffect(() => { api.get("/ui-config").then(({ data }) => setUrl(data.whatsapp_link || "")); }, []);
  if (url === null) return <Loader2 className="w-6 h-6 animate-spin text-rose-500" />;
  const save = async (v) => {
    setBusy(true);
    try { const { data } = await api.put("/admin/whatsapp-link", { url: v || null }); setUrl(data.url || ""); refreshUi(); toast.success(data.url ? "WhatsApp-Gruppenlink gespeichert" : "Link entfernt – Beitrittsknopf ist deaktiviert"); } catch (e) { toast.error(formatErr(e)); }
    setBusy(false);
  };
  const valid = !url || /^https:\/\/chat\.whatsapp\.com\/[A-Za-z0-9]+/.test(url.trim());
  return (
    <Card data-testid="admin-whatsapp">
      <h3 className="font-bold text-lg">WhatsApp-Gruppenlink</h3>
      <p className="text-xs text-slate-500">Dieser Einladungslink wird auf der Seite „Gemeinsam Punkte sammeln“ für die Beitrittsknöpfe verwendet. Ohne Link sind die Knöpfe deaktiviert. Erlaubt sind nur Links, die mit https://chat.whatsapp.com/ beginnen.</p>
      <input value={url} onChange={(e) => setUrl(e.target.value)} placeholder="https://chat.whatsapp.com/…" data-testid="admin-whatsapp-input" className="w-full h-11 px-4 rounded-xl border border-pink-200 text-sm" />
      {!valid && <p className="text-xs font-semibold text-red-600" data-testid="admin-whatsapp-invalid">Bitte einen gültigen WhatsApp-Einladungslink eingeben.</p>}
      <div className="flex flex-wrap gap-2">
        <button onClick={() => save(url.trim())} disabled={busy || !valid} data-testid="admin-whatsapp-save" className="h-10 px-5 rounded-full pink-gradient text-white text-sm font-bold disabled:opacity-50">Speichern</button>
        <button onClick={() => save(null)} disabled={busy} data-testid="admin-whatsapp-clear" className="h-10 px-5 rounded-full border-2 border-rose-200 text-rose-600 text-sm font-bold">Link entfernen</button>
        <a href="/gemeinsam-punkte-sammeln" data-testid="admin-whatsapp-page" className="h-10 px-5 rounded-full border-2 border-emerald-200 text-emerald-700 text-sm font-bold inline-flex items-center">Seite ansehen</a>
      </div>
      <WhatsAppStats />
    </Card>
  );
}

const SRC = { feed: "Feed-Knopf", hint: "Neulings-Hinweis", nudge: "Goldene Mitteilung", direct: "Direkt / Link" };

function WhatsAppStats() {
  const [s, setS] = useState(null);
  useEffect(() => { api.get("/admin/whatsapp/stats").then(({ data }) => setS(data)).catch(() => {}); }, []);
  if (!s) return <Loader2 className="w-5 h-5 animate-spin text-rose-500" />;
  const max = Math.max(1, ...s.daily.map((d) => d.visit));
  const tiles = [
    ["visit", "Seite besucht", s.visit.unique, `${s.visit.total} Aufrufe gesamt`],
    ["join", "Beitritt angetippt", s.join.unique, `${s.join.total} Klicks gesamt`],
    ["conversion", "Besucher → Beitritt", `${s.conversion} %`, "einzelne Mitglieder"],
    ["hint", "Neulings-Hinweis", s.hint_shown.unique, `${s.hint_open.unique} ${s.hint_open.unique === 1 ? "hat" : "haben"} „Gruppe ansehen“ getippt`],
  ];
  return (
    <div className="pt-4 border-t border-pink-100 space-y-4" data-testid="admin-whatsapp-stats">
      <h4 className="font-bold">Klickzähler</h4>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
        {tiles.map(([id, label, val, sub]) => (
          <div key={id} className="rounded-2xl bg-emerald-50/70 ring-1 ring-emerald-100 p-3" data-testid={`wa-stat-${id}`}>
            <p className="text-[11px] font-bold uppercase tracking-wide text-emerald-700">{label}</p>
            <p className="font-display text-2xl font-black text-slate-900 tabular-nums" data-testid={`wa-stat-${id}-value`}>{val}</p>
            <p className="text-[11px] text-slate-500">{sub}</p>
          </div>
        ))}
      </div>
      <div>
        <p className="text-xs font-bold text-slate-600 mb-2">Letzte 14 Tage (einzelne Mitglieder) · <span className="text-emerald-600">■ Besuche</span> <span className="text-rose-500">■ Beitritt</span></p>
        <div className="flex items-end gap-1 h-28" data-testid="wa-stat-chart">
          {s.daily.map((d) => (
            <div key={d.day} className="flex-1 flex flex-col items-center justify-end gap-0.5 h-full" title={`${d.day}: ${d.visit} Besuche, ${d.join} Beitritt`}>
              <div className="w-full flex items-end gap-px h-full">
                <span className="flex-1 rounded-t bg-emerald-400" style={{ height: `${(d.visit / max) * 100}%` }} />
                <span className="flex-1 rounded-t bg-rose-400" style={{ height: `${(d.join / max) * 100}%` }} />
              </div>
              <span className="text-[9px] text-slate-400">{d.day.slice(8)}</span>
            </div>
          ))}
        </div>
      </div>
      <p className="text-xs text-slate-500" data-testid="wa-stat-sources">Woher die Besuche kommen: {Object.keys(SRC).map((k) => `${SRC[k]} ${s.sources[k] || 0}`).join(" · ")}</p>
      <p className="text-[11px] text-slate-400">Admin- und Testkonten werden nicht mitgezählt. „Beitritt angetippt“ zählt Klicks auf den Knopf – ob jemand in WhatsApp wirklich beitritt, kann die App nicht sehen.</p>
    </div>
  );
}
